// src/components/library/LibraryPage.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  getFitnessLibraryTasks,
  getLekhiLibraryContent
} from '../../services/firebase/firestoreService';
import AudioView from '../lekhi/AudioView';
import TextView from '../lekhi/TextView';
import './LibraryPage.css';

const VideoModal = ({ url, onClose }) => {
  const getYouTubeId = (u) => {
    const match = u.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/|live\/)|youtu\.be\/)([^&\n?#/]+)/);
    return match ? match[1] : '';
  };
  return (
    <div className="video-overlay" onClick={onClose}>
      <div className="video-modal" onClick={e => e.stopPropagation()}>
        <button className="video-close" onClick={onClose}>✕</button>
        <div className="video-wrapper">
          <iframe src={`https://www.youtube.com/embed/${getYouTubeId(url)}`}
            title="Exercise Video" frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen/>
        </div>
      </div>
    </div>
  );
};

const FITNESS_FILTERS = [
  { id: 'all', mr: 'सर्व', en: 'All' },
  { id: 'athletics', mr: 'व्यायाम', en: 'Athletics' },
  { id: 'strength', mr: 'ताकद', en: 'Strength' },
  { id: 'nutrition', mr: 'आहार', en: 'Nutrition' }
];

const LEKHI_SUBJECT_FILTERS = [
  { id: 'all', mr: 'सर्व', en: 'All' },
  { id: 'grammar', mr: 'मराठी', en: 'Marathi' },
  { id: 'math', mr: 'गणित', en: 'Math' },
  { id: 'reasoning', mr: 'बुद्धिमत्ता', en: 'Reasoning' },
  { id: 'gk', mr: 'सामान्य ज्ञान', en: 'GK' }
];

const LEVEL_LABELS_MR = {
  easy: 'प्रारंभ',
  medium: 'प्रगत',
  hard: 'प्रावीण्य'
};

const TOPIC_LABELS_MR = {
  grammar: {
    jati: 'जाती', vibhakti: 'विभक्ती', samas: 'समास', alankar: 'अलंकार',
    prayog: 'प्रयोग', vachan: 'वचन', ling: 'लिंग', kaal: 'काळ',
    samanarthi: 'समानार्थी शब्द', virudharthi: 'विरुद्धार्थी शब्द',
    vakyaprachar: 'वाक्प्रचार', shuddhalekhan: 'शुद्धलेखन',
    viramchinhe: 'विरामचिन्हे', sandhi: 'संधी', sadrushya: 'साधर्म्य'
  },
  math: {
    simplification: 'सरलीकरण', lcm_hcf: 'ल.सा.वि. व म.सा.वि.', percentages: 'टक्केवारी',
    profit_loss: 'नफा-तोटा', si_ci: 'सरळ व चक्रवाढ व्याज', ratio: 'गुणोत्तर',
    average: 'सरासरी', time_work: 'काळ-काम-वेग', std: 'अंतर-काळ-वेग',
    pipes: 'नळ व टाक्या', area: 'क्षेत्रफळ', volume: 'घनफळ',
    geometry: 'भूमिती', squares_roots: 'वर्ग व वर्गमूळ', divisibility: 'विभाज्यता'
  },
  reasoning: {
    num_series: 'संख्या मालिका', letter_series: 'अक्षर मालिका',
    coding_decoding: 'सांकेतिक भाषा', blood_relations: 'नातेसंबंध',
    direction: 'दिशा', classification: 'वर्गीकरण', analogies: 'समानता',
    arrangement: 'मांडणी', word_arrangement: 'शब्द मांडणी', syllogism: 'न्यायनिवाडा',
    mirror: 'आरसा प्रतिमा', ranking: 'क्रमवारी', di: 'माहिती विश्लेषण',
    non_verbal: 'अशाब्दिक तर्क', problem_solving: 'समस्या निराकरण'
  },
  gk: {
    maha_history: 'महाराष्ट्र इतिहास', indian_history: 'भारतीय इतिहास',
    maha_geography: 'महाराष्ट्र भूगोल', indian_geography: 'भारतीय भूगोल',
    polity: 'राज्यशास्त्र', economy: 'अर्थशास्त्र', physics: 'भौतिकशास्त्र',
    chemistry: 'रसायनशास्त्र', biology: 'जीवशास्त्र', sports: 'क्रीडा',
    personalities: 'व्यक्तिमत्त्वे', awards: 'पुरस्कार',
    days_years: 'दिवस व वर्षे',
    current_affairs_national: 'राष्ट्रीय चालू घडामोडी',
    current_affairs_maharashtra: 'महाराष्ट्र चालू घडामोडी'
  }
};

const TYPE_LABELS = {
  strength: { mr: 'ताकद', en: 'Strength' },
  athletics: { mr: 'व्यायाम', en: 'Athletics' },
  nutrition: { mr: 'आहार', en: 'Nutrition' }
};

const TEXT = {
  title: { mr: 'लायब्ररी', en: 'Library' },
  tabFitness: { mr: 'व्यायाम', en: 'Fitness' },
  tabLekhi: { mr: 'लेखी', en: 'Lekhi' },
  searchFitness: { mr: 'व्यायाम शोधा...', en: 'Search workouts...' },
  searchLekhi: { mr: 'विषय शोधा...', en: 'Search topics...' },
  loading: { mr: 'लोड होत आहे...', en: 'Loading...' },
  emptyFitness: { mr: 'व्हिडिओ उपलब्ध नाहीत.', en: 'No videos available.' },
  emptyLekhi: { mr: 'लेखी पूर्ण करा आणि इथे साहित्य मिळेल.', en: 'Complete Lekhi to see content here.' },
  noResults: { mr: 'काही सापडले नाही.', en: 'Nothing found.' },
  audio1: { mr: 'शिकवणी (ऑडिओ १)', en: 'Audio 1' },
  audio2: { mr: 'चुका टाळा (ऑडिओ २)', en: 'Audio 2' },
  text: { mr: 'पाठ्यपुस्तक', en: 'Text' }
};

const getYouTubeThumbnail = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?|shorts)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/);
  return match ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg` : null;
};

const parseAudioOrder = (fileName) => {
  if (!fileName) return null;
  const lower = fileName.toLowerCase();
  if (lower.includes('audio1')) return 1;
  if (lower.includes('audio2')) return 2;
  return null;
};

export default function LibraryPage({ setHideBottomNav }) {
  const { currentUser } = useAuth();
  const [language, setLanguage] = useState(() =>
    localStorage.getItem('spocademy_language') || 'mr'
  );
  const t = (key) => TEXT[key][language] || TEXT[key].mr;

  const handleLanguageToggle = () => {
    const newLang = language === 'en' ? 'mr' : 'en';
    setLanguage(newLang);
    localStorage.setItem('spocademy_language', newLang);
  };

  const [activeTab, setActiveTab] = useState('fitness');
  const [fitnessTasks, setFitnessTasks] = useState([]);
  const [lekhiLibrary, setLekhiLibrary] = useState({});
  const [loadingFitness, setLoadingFitness] = useState(true);
  const [loadingLekhi, setLoadingLekhi] = useState(true);

  const [fitnessSearch, setFitnessSearch] = useState('');
  const [fitnessFilter, setFitnessFilter] = useState('all');

  const [lekhiSearch, setLekhiSearch] = useState('');
  const [lekhiFilter, setLekhiFilter] = useState('all');
  const [expandedTopics, setExpandedTopics] = useState(new Set());

  const [playingAudio, setPlayingAudio] = useState(null);
  const [libraryVideoUrl, setLibraryVideoUrl] = useState(null);
  const [readingText, setReadingText] = useState(null);

  useEffect(() => {
    const loadFitness = async () => {
      try {
        const data = await getFitnessLibraryTasks();
        setFitnessTasks(data);
      } catch (e) {} finally {
        setLoadingFitness(false);
      }
    };
    loadFitness();
  }, []);

  useEffect(() => {
    if (!currentUser?.uid) return;
    const loadLekhi = async () => {
      try {
        const data = await getLekhiLibraryContent(currentUser.uid);
        setLekhiLibrary(data);
      } catch (e) {} finally {
        setLoadingLekhi(false);
      }
    };
    loadLekhi();
  }, [currentUser]);

  useEffect(() => {
    if (setHideBottomNav) {
      setHideBottomNav(playingAudio !== null || readingText !== null);
    }
    return () => {
      if (setHideBottomNav) setHideBottomNav(false);
    };
  }, [playingAudio, readingText, setHideBottomNav]);

  const toggleTopicExpand = (key) => {
    setExpandedTopics(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleFitnessTap = (task) => {
    if (task.youtubeURL) {
      setLibraryVideoUrl(task.youtubeURL);
    }
  };

  // ===== FITNESS BUCKET RENDER =====
  const getFilteredFitnessTasks = () => {
    let filtered = fitnessTasks;
    if (fitnessFilter !== 'all') {
      filtered = filtered.filter(t => t.type === fitnessFilter);
    }
    if (fitnessSearch.trim()) {
      const q = fitnessSearch.trim().toLowerCase();
      filtered = filtered.filter(t => {
        const nameMr = (t.name?.mr || '').toLowerCase();
        const nameEn = (t.name?.en || '').toLowerCase();
        const desc = (t.description || '').toLowerCase();
        return nameMr.includes(q) || nameEn.includes(q) || desc.includes(q);
      });
    }
    return filtered;
  };

  const renderFitnessTab = () => {
    if (loadingFitness) {
      return <div className="lib-status">{t('loading')}</div>;
    }
    const filtered = getFilteredFitnessTasks();
    if (fitnessTasks.length === 0) {
      return <div className="lib-status">{t('emptyFitness')}</div>;
    }
    return (
      <>
        <input
          type="text"
          className="lib-search"
          placeholder={t('searchFitness')}
          value={fitnessSearch}
          onChange={e => setFitnessSearch(e.target.value)}
        />
        <div className="lib-chip-row">
          {FITNESS_FILTERS.map(f => (
            <button
              key={f.id}
              className={`lib-chip lib-chip-fitness ${fitnessFilter === f.id ? 'active' : ''}`}
              onClick={() => setFitnessFilter(f.id)}
            >
              {f[language] || f.mr}
            </button>
          ))}
        </div>
        {filtered.length === 0 ? (
          <div className="lib-status">{t('noResults')}</div>
        ) : (
          <div className="lib-fitness-list">
            {filtered.map(task => {
              const thumb = getYouTubeThumbnail(task.youtubeURL);
              const typeLabel = TYPE_LABELS[task.type]?.[language] || task.type;
              return (
                <div key={task.id} className="lib-fitness-card" onClick={() => handleFitnessTap(task)}>
                  <div className="lib-fitness-thumb">
                    {thumb ? (
                      <img src={thumb} alt={task.name} />
                    ) : (
                      <div className="lib-fitness-thumb-placeholder">▶</div>
                    )}
                  </div>
                  <div className="lib-fitness-info">
                    <div className="lib-fitness-name">
                      {task.name?.[language] || task.name?.mr || task.name?.en || ''}
                    </div>
                    {task.description && (
                      <div className="lib-fitness-desc">{task.description}</div>
                    )}
                    <div className="lib-fitness-type">{typeLabel}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  };

  // ===== LEKHI BUCKET RENDER =====
  const buildLekhiRows = () => {
    const rows = [];
    const subjectsToShow = lekhiFilter === 'all'
      ? ['grammar', 'math', 'reasoning', 'gk']
      : [lekhiFilter];

    subjectsToShow.forEach(subject => {
      const topicMap = lekhiLibrary[subject] || {};
      const topics = Object.keys(topicMap);
      topics.forEach(topic => {
        const levelMap = topicMap[topic] || {};
        Object.keys(levelMap).forEach(level => {
          const items = levelMap[level] || [];
          if (items.length === 0) return;
          const topicLabel = TOPIC_LABELS_MR[subject]?.[topic] || topic;
          rows.push({
            key: `${subject}|${topic}|${level}`,
            subject,
            topic,
            level,
            topicLabel,
            levelLabel: LEVEL_LABELS_MR[level] || level,
            items
          });
        });
      });
    });

    if (lekhiSearch.trim()) {
      const q = lekhiSearch.trim().toLowerCase();
      return rows.filter(r => r.topicLabel.toLowerCase().includes(q));
    }
    return rows;
  };

  const renderLekhiTab = () => {
    if (loadingLekhi) {
      return <div className="lib-status">{t('loading')}</div>;
    }
    const allRows = buildLekhiRows();
    const hasAnyContent = Object.values(lekhiLibrary).some(s => Object.keys(s).length > 0);

    if (!hasAnyContent) {
      return <div className="lib-status">{t('emptyLekhi')}</div>;
    }

    return (
      <>
        <input
          type="text"
          className="lib-search"
          placeholder={t('searchLekhi')}
          value={lekhiSearch}
          onChange={e => setLekhiSearch(e.target.value)}
        />
        <div className="lib-chip-row">
          {LEKHI_SUBJECT_FILTERS.map(f => (
            <button
              key={f.id}
              className={`lib-chip lib-chip-lekhi ${lekhiFilter === f.id ? 'active' : ''}`}
              onClick={() => setLekhiFilter(f.id)}
            >
              {f[language] || f.mr}
            </button>
          ))}
        </div>
        {allRows.length === 0 ? (
          <div className="lib-status">{t('noResults')}</div>
        ) : (
          <div className="lib-lekhi-list">
            {allRows.map(row => {
              const isExpanded = expandedTopics.has(row.key);
              const audios = row.items.filter(it => it.type === 'audio');
              const texts = row.items.filter(it => it.type === 'text');
              let audio1 = audios.find(a => parseAudioOrder(a.fileName) === 1);
              let audio2 = audios.find(a => parseAudioOrder(a.fileName) === 2);
              if (!audio1 || !audio2) {
                const sorted = [...audios].sort((a, b) => {
                  const ta = a.createdAt?.seconds || 0;
                  const tb = b.createdAt?.seconds || 0;
                  return ta - tb;
                });
                audio1 = audio1 || sorted[0] || null;
                audio2 = audio2 || sorted.find(a => a !== audio1) || null;
              }
              const textItem = texts[0] || null;

              return (
                <div key={row.key} className="lib-lekhi-row">
                  <button
                    className={`lib-lekhi-header ${isExpanded ? 'expanded' : ''}`}
                    onClick={() => toggleTopicExpand(row.key)}
                  >
                    <span className="lib-lekhi-topic-name">{row.topicLabel}</span>
                    <span className="lib-lekhi-level-tag">{row.levelLabel}</span>
                    <span className="lib-lekhi-arrow">{isExpanded ? '▲' : '▼'}</span>
                  </button>
                  {isExpanded && (
                    <div className="lib-lekhi-items">
                      {audio1 && (
                        <button className="lib-lekhi-item" onClick={() => setPlayingAudio({ audio: audio1, label: t('audio1'), topic: row.topicLabel })}>
                          <span className="lib-lekhi-item-icon">🎧</span>
                          <span className="lib-lekhi-item-label">{t('audio1')}</span>
                        </button>
                      )}
                      {audio2 && (
                        <button className="lib-lekhi-item" onClick={() => setPlayingAudio({ audio: audio2, label: t('audio2'), topic: row.topicLabel })}>
                          <span className="lib-lekhi-item-icon">🎧</span>
                          <span className="lib-lekhi-item-label">{t('audio2')}</span>
                        </button>
                      )}
                      {textItem && (
                        <button className="lib-lekhi-item" onClick={() => setReadingText(textItem)}>
                          <span className="lib-lekhi-item-icon">📖</span>
                          <span className="lib-lekhi-item-label">{t('text')}</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  };

  // ===== AUDIO / TEXT VIEWER =====
  if (playingAudio) {
    return (
      <AudioView
        audio={playingAudio.audio}
        blockLabel={playingAudio.label}
        topicName={playingAudio.topic}
        language={language}
        onComplete={() => setPlayingAudio(null)}
        onBack={() => setPlayingAudio(null)}
      />
    );
  }

  if (readingText) {
    return (
      <TextView
        textContent={readingText}
        language={language}
        onComplete={() => setReadingText(null)}
        onBack={() => setReadingText(null)}
      />
    );
  }

  // ===== MAIN LIBRARY UI =====
  return (
    <div className="lib-container">
      <div className="lib-header">
        <div className="lib-header-title">{t('title')}</div>
        <button className="lang-toggle" onClick={handleLanguageToggle}>
          {language === 'en' ? 'मराठी' : 'EN'}
        </button>
      </div>

      <div className="lib-tabs">
        <button
          className={`lib-tab lib-tab-fitness ${activeTab === 'fitness' ? 'active' : ''}`}
          onClick={() => setActiveTab('fitness')}
        >
          {t('tabFitness')}
        </button>
        <button
          className={`lib-tab lib-tab-lekhi ${activeTab === 'lekhi' ? 'active' : ''}`}
          onClick={() => setActiveTab('lekhi')}
        >
          {t('tabLekhi')}
        </button>
      </div>

      <div className="lib-content">
        {activeTab === 'fitness' ? renderFitnessTab() : renderLekhiTab()}
      </div>

      {libraryVideoUrl && (
        <VideoModal url={libraryVideoUrl} onClose={() => setLibraryVideoUrl(null)} />
      )}
    </div>
  );
}